import { FeatureFlags, COLOR_INDICATORS_FLAG } from '../../componentModels/FeatureFlags';
import { DataRow, EffectiveDefinition, Features, Value, ValueAccessor } from '../../models';
import { accessorForReference } from '../../models/Accessor';
import { isNullOrUndefined } from '../../models/DataTable';
import { Schema } from '../../models/EffectiveDefinition';
import { Err, MetricsEngineError, Ok, Result } from '../../models/Result';
import { Nullable } from '../../models/utilityTypes';
import { IfElseType, isIfElseCalculation } from '../../transforms/calculations/IfElseCalculation';
import { SimplePredicateType, ComparisonOperator } from '../../transforms/calculations/SimplePredicateCalculation';
import { MEASURE_COLUMN_NAME } from '../metricValue';
import { getDefaultLineForThreshold, getThresholdFeature } from '../threshold';

export const STATUS_FEATURE_NAME = 'status';

export interface StatusSpec {
  column: string;
}

export interface ColorSpec {
  column: string;
}

export interface StatusFeature {
  readonly status: StatusSpec;
  readonly color: ColorSpec;
}

export const getStatusFeature = (features: Features): StatusFeature | undefined => {
  return features.status;
};

export enum ThresholdStatus {
  UNKNOWN = 'unknown',
  NEUTRAL = 'neutral',
  GOOD = 'good',
  BAD = 'bad',
}

export const isThresholdStatus = (input: string): input is ThresholdStatus => {
  return Object.keys(ThresholdStatus).indexOf(input.toUpperCase()) !== -1;
};

export interface SimpleStatus {
  above: ThresholdStatus;
  equal: ThresholdStatus;
  below: ThresholdStatus;
}

export type NullableStatus = Nullable<SimpleStatus>;

export const NullStatus: NullableStatus = { above: null, equal: null, below: null };

export const definitionWithStatus = (
  definition: EffectiveDefinition,
  status: SimpleStatus
): Result<EffectiveDefinition, MetricsEngineError> => {
  const thresholdFeature = getThresholdFeature(definition.features);

  if (thresholdFeature === undefined) {
    return Err(new MetricsEngineError('Metric definition must contain a threshold feature'));
  }

  const thresholdColumnName = getDefaultLineForThreshold(thresholdFeature)?.measure.column;

  if (thresholdColumnName === undefined) {
    return Err(new MetricsEngineError('Threshold must contain measure column name'));
  }

  const statusColumnName = `status::threshold::${thresholdColumnName}`;
  const colorColumnName = `color::threshold::${thresholdColumnName}`;

  const newFeatures = makeFeaturesWithStatus(definition, statusColumnName, colorColumnName);
  const newSchema = makeSchemaWithStatus(definition, status, thresholdColumnName, statusColumnName, colorColumnName);

  return Ok({ ...definition, features: newFeatures, schema: newSchema });
};

const makeFeaturesWithStatus = (
  definition: EffectiveDefinition,
  statusColumnName: string,
  colorColumnName: string
): Features => {
  return {
    ...definition.features,
    [STATUS_FEATURE_NAME]: {
      status: { column: statusColumnName },
      color: { column: colorColumnName },
    },
  };
};

const makeSchemaWithStatus = (
  definition: EffectiveDefinition,
  status: SimpleStatus,
  thresholdColumnName: string,
  statusColumnName: string,
  colorColumnName: string
): Schema => {
  return {
    ...definition.schema,
    [statusColumnName]: {
      type: 'string',
      name: statusColumnName,
      calculation: {
        type: IfElseType,
        branches: [
          {
            predicate: {
              type: SimplePredicateType,
              lhs: {
                column: MEASURE_COLUMN_NAME,
              },
              rhs: {
                column: thresholdColumnName,
              },
              operator: ComparisonOperator.GREATER_THAN,
            },
            result: status.above,
          },
          {
            predicate: {
              type: SimplePredicateType,
              lhs: {
                column: MEASURE_COLUMN_NAME,
              },
              rhs: {
                column: thresholdColumnName,
              },
              operator: ComparisonOperator.EQUAL,
            },
            result: status.equal,
          },
          {
            predicate: {
              type: SimplePredicateType,
              lhs: {
                column: MEASURE_COLUMN_NAME,
              },
              rhs: {
                column: thresholdColumnName,
              },
              operator: ComparisonOperator.LESS_THAN,
            },
            result: status.below,
          },
        ],
      },
    },
    [colorColumnName]: {
      type: 'string',
      name: colorColumnName,
      calculation: {
        type: IfElseType,
        branches: [
          {
            predicate: {
              type: SimplePredicateType,
              lhs: {
                column: statusColumnName,
              },
              rhs: ThresholdStatus.GOOD,
              operator: ComparisonOperator.EQUAL,
            },
            result: MetricsGoodColor,
          },
          {
            predicate: {
              type: SimplePredicateType,
              lhs: {
                column: statusColumnName,
              },
              rhs: ThresholdStatus.NEUTRAL,
              operator: ComparisonOperator.EQUAL,
            },
            result: MetricsNeutralColor,
          },
          {
            predicate: {
              type: SimplePredicateType,
              lhs: {
                column: statusColumnName,
              },
              rhs: ThresholdStatus.BAD,
              operator: ComparisonOperator.EQUAL,
            },
            result: MetricsBadColor,
          },
        ],
      },
    },
  };
};
export const MetricBlue = '#2A79AF';
export const MetricBlueLight = 'rgba(42, 121, 175, 0.4)';
export const MetricComparisonColor = '#b4b4b4';
export const MetricsGoodColor = '#008057';
export const MetricsNeutralColor = '#2A79AF';
export const MetricsBadColor = '#C93A47';
export const MetricsGrey = '#333';
// @tableau/widgets disabled color, not exported via TabStyles.
// See https://tableau.slack.com/archives/CH52R1J3C/p1646860889209319
export const MetricsDisabled = '#bfbfbf';

export interface ColorFeature {
  readonly column: string;
}

export const defaultColorAccessor = (_?: DataRow): string => {
  return MetricsNeutralColor;
};

export const buildColorAccessor = (effectiveDefinition: EffectiveDefinition): ValueAccessor<string> => {
  const status = getStatusFeature(effectiveDefinition.features);

  if (!status?.color?.column) {
    return defaultColorAccessor;
  }

  return (dataRow: DataRow) => (dataRow[status.color.column] as string) ?? MetricsNeutralColor;
};

export const getStatus = (
  effectiveDefinition: EffectiveDefinition,
  featureFlags: FeatureFlags,
  selectedRow?: DataRow
): ThresholdStatus => {
  const colorIndicatorsEnabled = featureFlags[COLOR_INDICATORS_FLAG];
  if (!colorIndicatorsEnabled) {
    return ThresholdStatus.UNKNOWN;
  }
  const statusFeature = getStatusFeature(effectiveDefinition.features);
  const status =
    (statusFeature && selectedRow && accessorForReference<ThresholdStatus>(statusFeature.status)(selectedRow)) ??
    ThresholdStatus.UNKNOWN;
  return status;
};

export const getColorForPoint = (effectiveDefinition: EffectiveDefinition, value?: DataRow): string | undefined => {
  const colorAccessor = buildColorAccessor(effectiveDefinition);
  return value ? colorAccessor(value) : undefined;
};

// assumes that the colorIndicators flag is enabled
export const getAuthoredStatus = (definition: EffectiveDefinition): NullableStatus => {
  let status: NullableStatus = {
    above: null,
    equal: null,
    below: null,
  };

  const columnName = definition.features.status?.status?.column;
  const column = !!columnName && definition.schema[columnName];
  if (!column || !isIfElseCalculation(column.calculation)) {
    return status;
  }

  const branches = column.calculation?.branches;
  if (!branches) {
    return status;
  }

  for (const branch of branches) {
    const operator = branch.predicate?.operator;
    switch (operator) {
      case ComparisonOperator.GREATER_THAN:
        status.above = getThresholdStatusFromValue(branch.result);
        break;
      case ComparisonOperator.EQUAL:
        status.equal = getThresholdStatusFromValue(branch.result);
        break;
      case ComparisonOperator.LESS_THAN:
        status.below = getThresholdStatusFromValue(branch.result);
        break;
      default:
        throw new Error('should not have reached here');
    }
  }

  return status;
};

// exported for testing
export const getThresholdStatusFromValue = (value: Value): ThresholdStatus => {
  if (isNullOrUndefined(value)) {
    return ThresholdStatus.UNKNOWN;
  }

  const stringValue = value.toString();
  if (isThresholdStatus(stringValue)) {
    return stringValue;
  }

  return ThresholdStatus.UNKNOWN;
};
